import '../styles.css';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import b from '../assets/Sample/adrien-bruneau-8cpR_Yf0rQs-unsplash.jpg';
import c from '../assets/STAMP-LOGO.webp';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import GoogleAuthButton from '../components/GoogleAuthButton';
import PasswordInput from '../components/PasswordInput';
import { requestPasswordReset, confirmPasswordReset } from '../api/auth';

// 동의는 체크박스가 아니라 이 문구가 받는다. 계정을 만드는 버튼 옆에 두고,
// 버튼을 누르는 행위가 동의가 된다(clickwrap) -- Google 자신의 consent 화면도
// 같은 두 링크를 보여주며, 그쪽은 Cloud Console의 Branding 설정에서 등록한다.
// 어느 쪽도 결과를 우리에게 알려주지 않으므로, 계정이 만들어진 시각이 곧
// 기록이 된다.
//
// target="_blank"인 이유: 같은 탭으로 약관을 열면 여기까지 채운 입력이
// 사라진다. rel은 새 탭과 한 쌍으로 붙인다.
function TermsNotice() {
    return (
        <p className="auth-terms-notice" data-testid="terms-notice">
            By continuing, you agree to our{' '}
            <Link
                to="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="auth-link"
            >
                Terms of Service
            </Link>{' '}
            and{' '}
            <Link
                to="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="auth-link"
            >
                Privacy Policy
            </Link>
            .
        </p>
    );
}

// Auth mode 타입 정의
const AUTH_MODES = {
    LOGIN: 'login',
    SIGNUP: 'signup',
    FORGOT_PASSWORD: 'forgot-password',
    RESET_PASSWORD: 'reset-password',
};

// 경로에서 mode 결정하는 함수
function getAuthMode(pathname) {
    if (pathname === '/signup') return AUTH_MODES.SIGNUP;
    if (pathname === '/forgot-password' || pathname === '/forgot')
        return AUTH_MODES.FORGOT_PASSWORD;
    if (pathname === '/reset-password' || pathname === '/reset')
        return AUTH_MODES.RESET_PASSWORD;
    return AUTH_MODES.LOGIN; // 기본값
}

// 각 mode별 CSS 클래스 매핑
function getModeClasses(mode) {
    const baseClass =
        mode === AUTH_MODES.SIGNUP
            ? 'signup'
            : mode === AUTH_MODES.FORGOT_PASSWORD
              ? 'forgot-password'
              : mode === AUTH_MODES.RESET_PASSWORD
                ? 'reset-password'
                : 'login';
    return {
        container: `${baseClass}-container`,
        background: `${baseClass}-background`,
        overlay: `${baseClass}-overlay`,
        widget: `${baseClass}-widget`,
        logo: `${baseClass}-logo`,
        form: `${baseClass}-form`,
        inputGroup: `${baseClass}-input-group`,
        input: `${baseClass}-input`,
    };
}

function getAuthLayoutVariables() {
    const viewportWidth =
        typeof window === 'undefined' ? 1280 : window.innerWidth;
    const viewportHeight =
        typeof window === 'undefined' ? 720 : window.innerHeight;
    const rem = (min, preferredPixels, max) =>
        `${Math.min(max, Math.max(min, preferredPixels / 16)).toFixed(3)}rem`;

    return {
        '--auth-widget-width': rem(18, viewportWidth * 0.251, 30),
        // 하한이 32rem일 때는 창이 560px 아래로 내려가면 카드의 최소 높이가
        // 화면에 남은 높이보다 커서, 콘텐츠와 무관하게 아래가 잘렸다. 이
        // 하한은 콘텐츠가 적은 forgot/reset이 납작해지지 않게 하려는 것이므로
        // 그만큼만 남긴다.
        '--auth-widget-height': rem(26, viewportHeight * 0.735, 47),
        '--auth-padding-x': rem(1, viewportWidth * 0.0152, 2.5),
        '--auth-padding-bottom': rem(1.5, viewportHeight * 0.0358, 3),
        '--auth-control-width': rem(15, viewportWidth * 0.223, 26),
        '--auth-input-group-gap': rem(0.4, viewportHeight * 0.0107, 1),
        '--auth-input-height': rem(2.8, viewportHeight * 0.054, 5),
        '--auth-control-font': rem(0.85, viewportWidth * 0.0104, 1.4),
        '--auth-input-padding': rem(0.75, viewportWidth * 0.0104, 1.5),
        '--auth-button-height': rem(2.2, viewportHeight * 0.038, 3.5),
        '--auth-secondary-gap': rem(0.3, viewportHeight * 0.009, 0.75),
        '--auth-link-font': rem(0.7, viewportWidth * 0.0081, 1),

        '--login-padding-top': rem(0.75, viewportHeight * 0.035, 6),
        // 로고만 뷰포트 너비로 재는데, 세로가 좁은 창에서는 그 폭이 그대로
        // 남아 카드에서 가장 큰 세로 공간을 차지한다. 짧은 쪽도 함께 보게
        // 해서 창이 납작할 때 로고부터 양보하도록 한다.
        '--login-logo-size': rem(
            4,
            Math.min(viewportWidth * 0.0723, viewportHeight * 0.115),
            9.5,
        ),
        '--login-logo-gap': rem(0.75, viewportHeight * 0.035, 6),
        '--login-input-gap': rem(0.9, viewportHeight * 0.0304, 3),
        '--login-forgot-top': rem(0.4, viewportHeight * 0.0116, 1.5),
        '--login-forgot-bottom': rem(0.75, viewportHeight * 0.05, 8),
        '--login-divider-top': rem(0.6, viewportHeight * 0.033, 5.5),
        '--login-divider-bottom': rem(0.75, viewportHeight * 0.0358, 4),

        '--signup-padding-top': rem(0.75, viewportHeight * 0.028, 5),
        '--signup-logo-size': rem(
            2,
            Math.min(viewportWidth * 0.0405, viewportHeight * 0.065),
            5.5,
        ),
        '--signup-logo-gap': rem(0.4, viewportHeight * 0.0116, 1.5),
        '--signup-title-size': rem(1.1, viewportWidth * 0.0185, 2.5),
        '--signup-title-gap': rem(0.75, viewportHeight * 0.025, 4),
        // 체크박스가 있던 자리다. 문구로 바뀌면서 비었으므로 원래 폭을
        // 유지할 이유가 없다.
        '--signup-create-gap': rem(0.75, viewportHeight * 0.045, 10),
        '--signup-divider-top': rem(1, viewportHeight * 0.0278, 4),
        // Google renders its button inside an iframe and sizes it itself, so
        // there is no height to set here any more, and the row holds a single
        // child so there is no gap either. Only the row's own offset is still
        // ours to decide.
        '--signup-oauth-top': rem(0.5, viewportHeight * 0.0134, 2),

        '--forgot-padding-top': rem(2, viewportHeight * 0.102, 14),
        '--forgot-logo-size': rem(3, viewportWidth * 0.0573, 8),
        '--forgot-logo-gap': rem(0.75, viewportHeight * 0.0215, 3),
        '--forgot-title-size': rem(1.1, viewportWidth * 0.0185, 2.5),
        '--forgot-title-gap': rem(0.5, viewportHeight * 0.0116, 1.5),
        '--forgot-description-font': rem(0.8, viewportWidth * 0.0093, 1.1),
        '--forgot-description-gap': rem(0.6, viewportHeight * 0.0179, 2),
        '--forgot-button-gap': rem(1.5, viewportHeight * 0.0447, 5),
        '--forgot-link-gap': rem(0.75, viewportHeight * 0.0188, 2.5),

        '--reset-padding-top': rem(2, viewportHeight * 0.102, 14),
        '--reset-logo-size': rem(3, viewportWidth * 0.0573, 8),
        '--reset-logo-gap': rem(0.75, viewportHeight * 0.0215, 3),
        '--reset-title-size': rem(1.1, viewportWidth * 0.0185, 2.5),
        '--reset-title-gap': rem(1.5, viewportHeight * 0.0447, 5),
        '--reset-input-gap': rem(0.9, viewportHeight * 0.0304, 3),
        '--reset-button-gap': rem(1.5, viewportHeight * 0.0501, 6),
        '--reset-link-gap': rem(0.75, viewportHeight * 0.0179, 2.5),
    };
}

export default function Auth() {
    const location = useLocation();
    const navigate = useNavigate();
    const mode = getAuthMode(location.pathname);
    const classes = getModeClasses(mode);

    const { login, signup, googleLogin, googleLink, user } = useAuth();
    const [authLayoutVariables] = useState(getAuthLayoutVariables);

    // 재로그인 모달의 [로그인] 버튼이 넘긴 state로 만료 안내를 표시.
    const sessionExpired = Boolean(location.state?.sessionExpired);

    // 체크인 QR을 찍고 온 경우. 스스로 로그인하러 온 게 아니라 체크인 도중에
    // 거쳐가는 화면이므로, 왜 묻는지와 로그인 후 무슨 일이 일어나는지를 알린다.
    const checkIn = Boolean(location.state?.checkIn);

    // 이미 로그인된 사용자가 로그인 페이지에 접근하면 홈으로 이동
    useEffect(() => {
        if (mode === AUTH_MODES.LOGIN && user) {
            const from = location.state?.from?.pathname || '/';
            navigate(from, { replace: true });
        }
    }, [mode, user, location.state, navigate]);

    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const [searchParams] = useSearchParams();
    const [resetForm, setResetForm] = useState({
        email: '',
        password: '',
        confirmPassword: '',
    });
    // 인라인 표시. 기존 login/signup은 alert()를 쓰지만 새로 쓰는 화면이라
    // 여기서 패턴을 올린다. 기존 alert() 호출부는 이번 스코프가 아니다.
    const [formError, setFormError] = useState('');
    const [resetSent, setResetSent] = useState(false);

    // 409에서 받은 것들. URL이 바뀌지 않으므로 mode가 아니라 state로 화면을
    // 가른다. null이면 평소의 mode 기반 화면이 그대로 나온다.
    const [googleConflict, setGoogleConflict] = useState(null);
    const [linkChoice, setLinkChoice] = useState({
        keepPassword: true,
        password: '',
    });

    const handleResetChange = (e) => {
        setResetForm({ ...resetForm, [e.target.name]: e.target.value });
        setFormError('');
    };

    const handleForgotSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        try {
            await requestPasswordReset({ email: resetForm.email });
        } catch (error) {
            // 의도적으로 무시한다. 서버는 모르는 주소에도 200을 주므로, 여기서
            // 전송 오류를 노출하면 계정 존재 여부를 구분하는 유일한 통로가 된다.
        }
        setResetSent(true);
    };

    const handleResetSubmit = async (e) => {
        e.preventDefault();
        if (resetForm.password !== resetForm.confirmPassword) {
            setFormError('Passwords do not match.');
            return;
        }
        try {
            await confirmPasswordReset({
                uid: searchParams.get('uid'),
                token: searchParams.get('token'),
                password: resetForm.password,
            });
            navigate('/login');
        } catch (error) {
            const data = error.response?.data;
            setFormError(
                data?.detail ||
                    data?.new_password?.[0] ||
                    'Something went wrong. Please try again.',
            );
        }
    };

    const handleLogin = async (e) => {
        if (e) e.preventDefault();
        try {
            await login({ email: formData.email, password: formData.password });
            const from = location.state?.from?.pathname || '/';
            navigate(from, { replace: true });
        } catch (error) {
            alert(
                'Login failed: ' +
                    (error.response?.data?.detail || 'Unknown error'),
            );
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setFormError('');
        try {
            await signup({
                username: formData.username || formData.email,
                email: formData.email,
                password: formData.password,
            });
            alert('Account created! Please log in.');
            navigate('/login');
        } catch (error) {
            alert('Signup failed: ' + JSON.stringify(error.response?.data));
        }
    };

    // GIS renders the button inside a cross-origin iframe, so none of this can
    // be done in CSS -- these props are the only control Google offers.
    // `width` is a plain pixel number, not a fluid unit, so it has to be
    // computed rather than hardcoded: a literal lines up at exactly one
    // viewport width and is visibly off at every other.
    //
    // Every other control here is `width: 100%` with
    // `max-width: var(--auth-control-width)`, so what it actually renders at is
    // whichever of the two is smaller -- and above ~1280px the form (card width
    // minus its horizontal padding) is the narrower one. Reading only the
    // max-width would make this button 3-4px wider than the buttons beside it.
    const layoutPx = (name) => parseFloat(authLayoutVariables[name]) * 16;
    const googleButtonWidth = Math.min(
        // GIS refuses anything wider; past ~1900px the row is 16px short of the
        // other buttons and there is no way around it.
        400,
        Math.round(
            Math.min(
                layoutPx('--auth-control-width'),
                layoutPx('--auth-widget-width') - 2 * layoutPx('--auth-padding-x'),
            ),
        ),
    );
    const goWhereTheyWereHeaded = () => {
        navigate(location.state?.from?.pathname || '/', { replace: true });
    };

    const handleGoogleError = () => {
        // GIS는 실패 사유를 넘겨주지 않는다. 팝업 차단, 사용자의 취소, origin
        // 미등록이 전부 이 한 콜백으로 들어온다.
        setFormError('Google sign-in was cancelled or blocked. Please try again.');
    };

    const handleGoogleSuccess = async ({ credential }) => {
        setFormError('');
        // credential은 타입상 optional이다. 없는 채로 보내면 서버가 401을 준다.
        if (!credential) {
            handleGoogleError();
            return;
        }
        try {
            await googleLogin({ credential });
            goWhereTheyWereHeaded();
        } catch (error) {
            // 409는 인터셉터가 건드리지 않고 그대로 reject로 온다(인터셉터는
            // 401만 처리). 실패가 아니라 사용자에게 물어볼 것이 생긴 상태다.
            const data = error.response?.data;
            if (error.response?.status === 409) {
                setGoogleConflict({
                    linkToken: data.link_token,
                    email: data.email,
                    hasUsablePassword: Boolean(data.has_usable_password),
                });
                // 살릴 비밀번호가 없으면 그 선택지는 아예 없으므로 기본값도
                // 구글 전용이어야 한다.
                setLinkChoice({
                    keepPassword: Boolean(data.has_usable_password),
                    password: '',
                });
                return;
            }
            setFormError(
                data?.detail || 'Google sign-in failed. Please try again.',
            );
        }
    };

    const handleGoogleLinkSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        if (linkChoice.keepPassword && !linkChoice.password) {
            setFormError('Enter your password to keep using it.');
            return;
        }
        try {
            await googleLink({
                linkToken: googleConflict.linkToken,
                keepPassword: linkChoice.keepPassword,
                password: linkChoice.password,
            });
            goWhereTheyWereHeaded();
        } catch (error) {
            // 서버는 대부분의 거부를 같은 문구로 가리지만, 비밀번호 불일치만은
            // 따로 알려준다 -- 그건 다시 입력하면 풀리는 문제이기 때문이다.
            // 어느 쪽이든 문구를 그대로 보여주고 화면은 유지한다. 비밀번호 칸은
            // 비워서 재입력임이 분명하게.
            setFormError(
                error.response?.data?.detail ||
                    'Could not link this account. Please sign in with Google again.',
            );
            setLinkChoice((prev) => ({ ...prev, password: '' }));
        }
    };

    const cancelGoogleLink = () => {
        setGoogleConflict(null);
        setFormError('');
    };

    // Login Form 렌더링
    const renderLoginForm = () => (
        <>
            <div className={`auth-form ${classes.form}`}>
                {sessionExpired && (
                    <p className="auth-session-expired" role="alert">
                        Your session has expired. Please log in again.
                    </p>
                )}
                {checkIn && (
                    <p className="auth-form-notice" role="status">
                        Log in to check in — we'll record your attendance right after.
                    </p>
                )}
                {formError && (
                    <p className="auth-form-error" role="alert">
                        {formError}
                    </p>
                )}
                <div className={`auth-input-group ${classes.inputGroup}`}>
                    <input
                        type="text"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`auth-input ${classes.input}`}
                        placeholder="Email"
                    />
                </div>
                <div className={`auth-input-group ${classes.inputGroup}`}>
                    <PasswordInput
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={`auth-input ${classes.input}`}
                        placeholder="Password"
                    />
                </div>
                <Link
                    to="/forgot-password"
                    className="auth-link login-forgot-link"
                >
                    Forgot Password?
                </Link>
                <button
                    onClick={handleLogin}
                    className="auth-button-primary login-button"
                >
                    Login
                </button>
                <hr className="auth-divider login-divider" />

                <Link to="/signup" className="auth-link-button-wrapper">
                    <button className="auth-button-secondary sign-in-button">
                        Join us
                    </button>
                </Link>
                <div className="auth-google-button">
                    <GoogleAuthButton
                        text="signin_with"
                        width={googleButtonWidth}
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                    />
                </div>
                {/* 로그인 화면이지만 이 버튼으로 계정이 만들어지기도 한다.
                    고지가 가입 화면에만 있으면 그 경로는 비어 있게 된다. */}
                <TermsNotice />
            </div>
        </>
    );

    // Signup Form 렌더링
    const renderSignupForm = () => (
        <>
            <h1 className="signup-title">Create an Account</h1>

            <div className="signup-login-link">
                <span className="signup-have-account">
                    Already have an account?
                </span>
                <Link to="/login" className="auth-link signup-login-text">
                    Log in
                </Link>
            </div>

            <div className={`auth-form ${classes.form}`}>
                {formError && (
                    <p className="auth-form-error" role="alert">
                        {formError}
                    </p>
                )}
                <div className={`auth-input-group ${classes.inputGroup}`}>
                    <input
                        type="text"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        className={`auth-input ${classes.input}`}
                        placeholder="Username"
                    />
                </div>
                <div className={`auth-input-group ${classes.inputGroup}`}>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`auth-input ${classes.input}`}
                        placeholder="Email"
                    />
                </div>
                <div className={`auth-input-group ${classes.inputGroup}`}>
                    <PasswordInput
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={`auth-input ${classes.input}`}
                        placeholder="Password"
                    />
                </div>
                <button
                    onClick={handleSignup}
                    className="auth-button-primary signup-create-button"
                >
                    Create
                </button>
            </div>

            <div className="auth-divider-with-text signup-divider">
                <div className="auth-divider-line signup-divider-line"></div>
                <span className="auth-divider-text signup-divider-text">
                    Or with
                </span>
                <div className="auth-divider-line signup-divider-line"></div>
            </div>

            <div className="signup-oauth-buttons auth-google-button">
                <GoogleAuthButton
                        text="signup_with"
                        width={googleButtonWidth}
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                    />
            </div>
            <TermsNotice />
        </>
    );

    // Forgot Password Form 렌더링
    const renderForgotPasswordForm = () => (
        <>
            <h1 className="forgot-password-title">Forgot Password?</h1>
            <p className="forgot-password-description">
                Enter your email address and we'll send you a link to reset your
                password.
            </p>

            {resetSent ? (
                <p className="auth-form-notice" role="status">
                    If an account exists for that address, a reset link is on its way.
                </p>
            ) : (
                <form
                    className={`auth-form ${classes.form}`}
                    onSubmit={handleForgotSubmit}
                >
                    <div className={`auth-input-group ${classes.inputGroup}`}>
                        <input
                            type="email"
                            name="email"
                            value={resetForm.email}
                            onChange={handleResetChange}
                            className={`auth-input ${classes.input}`}
                            placeholder="Email"
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        className="auth-button-primary forgot-password-button"
                    >
                        Send Reset Link
                    </button>
                </form>
            )}

            <div className="forgot-password-login-link">
                <Link to="/login" className="auth-link">
                    Back to Login
                </Link>
            </div>
        </>
    );

    // Reset Password Form 렌더링
    const renderResetPasswordForm = () => (
        <>
            <h1 className="reset-password-title">Reset Password</h1>

            <form className={`auth-form ${classes.form}`} onSubmit={handleResetSubmit}>
                {formError && (
                    <p className="auth-form-error" role="alert">
                        {formError}
                    </p>
                )}
                <div className={`auth-input-group ${classes.inputGroup}`}>
                    <PasswordInput
                        name="password"
                        value={resetForm.password}
                        onChange={handleResetChange}
                        className={`auth-input ${classes.input}`}
                        placeholder="New Password"
                        required
                    />
                </div>
                <div className={`auth-input-group ${classes.inputGroup}`}>
                    <PasswordInput
                        name="confirmPassword"
                        value={resetForm.confirmPassword}
                        onChange={handleResetChange}
                        className={`auth-input ${classes.input}`}
                        placeholder="Confirm New Password"
                        required
                    />
                </div>
                <button
                    type="submit"
                    className="auth-button-primary reset-password-button"
                >
                    Reset Password
                </button>
            </form>

            <div className="reset-password-login-link">
                <Link to="/login" className="auth-link">
                    Back to Login
                </Link>
            </div>
        </>
    );

    // 구글 연결 화면. 여기 온 사람은 구글 토큰으로 이 주소의 소유를 이미
    // 증명했다. 남은 질문은 "기존 비밀번호를 살릴 것인가" 하나뿐이고, 그건
    // 비밀번호를 아는지로만 갈린다.
    const renderGoogleLinkForm = () => (
        <>
            <h1 className="auth-heading">An account already exists</h1>
            <p className="auth-form-notice">
                {googleConflict.email} is already registered. Choose how you
                want to sign in from now on.
            </p>

            <form
                className={`auth-form ${classes.form}`}
                onSubmit={handleGoogleLinkSubmit}
            >
                {formError && (
                    <p className="auth-form-error" role="alert">
                        {formError}
                    </p>
                )}

                {googleConflict.hasUsablePassword && (
                    <label className="auth-choice">
                        <input
                            type="radio"
                            name="google-link-choice"
                            checked={linkChoice.keepPassword}
                            onChange={() =>
                                setLinkChoice({ keepPassword: true, password: '' })
                            }
                        />
                        Keep my password
                    </label>
                )}

                <label className="auth-choice">
                    <input
                        type="radio"
                        name="google-link-choice"
                        checked={!linkChoice.keepPassword}
                        onChange={() =>
                            setLinkChoice({ keepPassword: false, password: '' })
                        }
                    />
                    Use Google only
                </label>

                {linkChoice.keepPassword && (
                    <div className={`auth-input-group ${classes.inputGroup}`}>
                        <PasswordInput
                            className={`auth-input ${classes.input}`}
                            placeholder="Password"
                            value={linkChoice.password}
                            onChange={(e) =>
                                setLinkChoice({
                                    keepPassword: true,
                                    password: e.target.value,
                                })
                            }
                        />
                    </div>
                )}

                <button type="submit" className="auth-button-primary">
                    Continue
                </button>
                <button
                    type="button"
                    className="auth-button-secondary"
                    onClick={cancelGoogleLink}
                >
                    Cancel
                </button>
            </form>
        </>
    );

    // Mode에 따라 적절한 Form 렌더링
    const renderForm = () => {
        // 충돌 화면은 경로가 아니라 응답에서 생기므로 mode보다 앞선다.
        if (googleConflict) return renderGoogleLinkForm();
        switch (mode) {
            case AUTH_MODES.SIGNUP:
                return renderSignupForm();
            case AUTH_MODES.FORGOT_PASSWORD:
                return renderForgotPasswordForm();
            case AUTH_MODES.RESET_PASSWORD:
                return renderResetPasswordForm();
            default:
                return renderLoginForm();
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <div
                className={`auth-container ${classes.container}`}
                style={authLayoutVariables}
            >
                <div className={`auth-background ${classes.background}`}>
                    {/* Decorative -- an empty alt keeps screen readers from
                        announcing a background that carries no information. */}
                    <img src={b} alt="" />
                    <div className={`auth-overlay ${classes.overlay}`}></div>
                </div>

                <div className={`auth-widget ${classes.widget}`}>
                    <img
                        src={c}
                        className={`auth-logo ${classes.logo}`}
                        alt="STAMP-LOGO"
                    />
                    {renderForm()}
                </div>
            </div>
        </div>
    );
}
